<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-btn variant="text" @click="goBack" class="mb-4">
          <v-icon start :icon="mdiArrowLeft" />
          Voltar
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="isLoading">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" />
      </v-col>
    </v-row>

    <template v-else-if="details.length > 0">
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>Informações da Loja</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="mb-2">
                    <strong>Razão Social:</strong>
                    {{ storeData?.loja_razao_social }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-2">
                    <strong>CNPJ:</strong>
                    {{ formatCnpj(storeData?.loja_cnpj) }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-2">
                    <strong>Total de Dispositivos:</strong>
                    {{ details.length }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="mb-2">
                    <strong>Software House/Revenda:</strong>
                    {{ storeData?.tenant_name || "Não vinculado" }}
                  </div>
                </v-col>
                <v-col v-if="storeData?.assigned_at_utc" cols="12" md="6">
                  <div class="mb-2">
                    <strong>Data de Vinculação:</strong>
                    {{ formatDate(storeData.assigned_at_utc) }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions v-if="hasUnassignedDevices">
              <v-spacer />
              <v-btn color="primary" @click="openAssignDialog">
                Vincular Dispositivos
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>Dispositivos SAT</v-card-title>
            <v-card-text>
              <v-data-table
                :headers="deviceHeaders"
                :items="details"
                :items-per-page="10"
                :items-per-page-options="[
                  { value: 10, title: '10' },
                  { value: 20, title: '20' },
                  { value: 40, title: '40' },
                  { value: 50, title: '50' },
                ]"
              >
                <template v-slot:item.assigned="{ item }">
                  <v-icon
                    :color="item.assigned ? 'success' : 'warning'"
                    size="small"
                    :icon="item.assigned ? mdiCheckCircle : mdiClock"
                  />
                  {{ item.assigned ? "Vinculado" : "Pendente" }}
                </template>

                <template v-slot:item.oauth2_device_id="{ item }">
                  {{ item.oauth2_device_id || "-" }}
                </template>

                <template v-slot:item.created_utc="{ item }">
                  {{ formatDate(item.created_utc) }}
                </template>

                <template v-slot:item.assigned_at_utc="{ item }">
                  {{
                    item.assigned_at_utc
                      ? formatDate(item.assigned_at_utc)
                      : "-"
                  }}
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-row v-else>
      <v-col cols="12">
        <v-alert type="info">
          Nenhum dispositivo encontrado para este CNPJ.
        </v-alert>
      </v-col>
    </v-row>

    <!-- Assignment Dialog -->
    <preregistration-assign-dialog
      v-if="storeData"
      v-model="showAssignDialog"
      :store="storeData"
      @success="onAssignSuccess"
    />
  </v-container>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { mdiArrowLeft, mdiCheckCircle, mdiClock } from "@mdi/js";
import { useRoute, useRouter } from "vue-router";
import PreregistrationAssignDialog from "./PreregistrationAssignDialog.vue";
import { usePreregistrationStoreDevicesQuery } from "@/queries/modules/preregistrations";
import { useSnackbar } from "@/plugins/snackbar";
import { useMetaStore } from "@/stores/meta";

const route = useRoute();
const router = useRouter();
const snackbar = useSnackbar();
const metaStore = useMetaStore();

// Refs
const showAssignDialog = ref(false);

// Table headers (Vuetify 3 format)
const deviceHeaders = [
  { title: "ID", key: "id" },
  { title: "Número de Série SAT", key: "sat_serial_number" },
  { title: "Status", key: "assigned" },
  { title: "OAuth2 Device ID", key: "oauth2_device_id" },
  { title: "Data de Criação", key: "created_utc" },
  { title: "Data de Vinculação", key: "assigned_at_utc" },
];

// Computed
const lojaCnpj = computed(() => route.params.loja_cnpj || null);

// TanStack Query - fetch store device details
const {
  data: detailsData,
  isLoading,
  error,
  refetch,
} = usePreregistrationStoreDevicesQuery(lojaCnpj);

// Computed properties
const details = computed(() => detailsData.value || []);

const hasUnassignedDevices = computed(() => {
  return details.value.some((device) => !device.assigned);
});

const storeData = computed(() => {
  if (details.value.length === 0) return null;

  const assignedDevice = details.value.find((d) => d.tenant_name);
  let tenant_name = null;
  let assigned_at_utc = null;
  if (assignedDevice) {
    tenant_name = assignedDevice.tenant_name;
    assigned_at_utc = assignedDevice.assigned_at_utc;
  }

  return {
    loja_razao_social: details.value[0].loja_razao_social,
    loja_cnpj: details.value[0].loja_cnpj,
    device_count: details.value.length,
    tenant_name: tenant_name,
    assigned_at_utc: assigned_at_utc,
    devices: details.value.map((device) => ({
      id: device.id,
      assigned: device.assigned,
      tenant_id: device.tenant_id,
    })),
  };
});

// Format CNPJ helper
const formatCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Update page title when store data changes
watch(
  storeData,
  (data) => {
    if (data) {
      metaStore.setTitle(`Detalhes - ${data.loja_razao_social}`);
    } else {
      metaStore.setTitle("Detalhes - Loja");
    }
  },
  { immediate: true },
);

// Watch for errors
watch(error, (err) => {
  if (err) {
    snackbar.error(err.message);
  }
});

// Methods
const goBack = () => {
  router.push({ name: "preregistrations.index" });
};

const openAssignDialog = () => {
  showAssignDialog.value = true;
};

const onAssignSuccess = () => {
  refetch();
};
</script>

<style scoped>
.mb-2 {
  margin-bottom: 8px;
}
</style>
